/**
 * The payment permissions (docs/payments/CONTRACT.md §4). PAY-CORE adds the keys on the API; Super Admin's '*' and a
 * role's 'billing.*' cover every one (usePermissions → permissionGranted, the API guard's rule). A screen hides what a
 * role cannot do; the API refuses it regardless.
 */
export const PAY_PERM = {
  /** Collect & renew at the counter. */
  collect: 'billing.collect',
  /** Received on older than 3 days. */
  backdate: 'billing.backdate',
  /** Renew on credit within the limit. */
  payLater: 'billing.pay_later',
  /** Approve credit above the limit (Accounts). */
  payLaterApprove: 'billing.pay_later.approve',
  voidRequest: 'billing.receipt.void.request',
  voidApprove: 'billing.receipt.void.approve',
  unapplied: 'billing.unapplied.manage',
  collectionPoints: 'billing.collection_points.manage',
  chequeBounce: 'billing.cheque.bounce',
  /**
   * The free extension under the One renewal policy (pay/core d178a806: the final key; subscribers.extend_free is gone).
   * The API answers 403 extend_free_required without it.
   */
  extendFree: 'extensions.free',
  /** No-loss corrections (spec §0.5): ask for a credit note, bill reduction, refund or write-off; raise a debit note. */
  correctionsRequest: 'billing.corrections.request',
  /** Approve or reject corrections up to the Accounts limit (₹1,000 by default). Above it: the Super Admin only. */
  correctionsApprove: 'billing.corrections.approve',
} as const;

/**
 * The Super Admin — the Owner tier of corrections, the Trusted customer settings and the no-loss policy fields — exactly
 * the API's isSuperAdmin (speed-override-rules.ts; behaviour review B-m12): a staff login that holds '*' itself, not a
 * partner's login, not staff acting through Login as Partner (impersonation), not a subscriber token. A module wildcard
 * ('billing.*') is not it.
 */
export function isSuperAdmin(user: {
  permissions?: readonly string[] | null; partnerId?: string | null; impersonation?: unknown; type?: string | null;
} | null | undefined): boolean {
  if (!user || user.partnerId || user.impersonation || user.type === 'subscriber') return false;
  return Array.isArray(user.permissions) && user.permissions.includes('*');
}


/** The receipt PDF needs billing.read; resending it billing.create or billing.collect (PAY-CORE's routes). */
export const RECEIPT_READ = 'billing.read';
export const RECEIPT_RESEND = ['billing.create', 'billing.collect'] as const;
